import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowLeft, Clock, Share2, Bookmark } from 'lucide-react';
import { blogAPI } from '../services/api';
import { format } from 'date-fns';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await blogAPI.getBySlug(slug);
      setPost(response.data.blog);
      setRelatedPosts(response.data.relatedBlogs || []);
    } catch (error) {
      console.error('Error fetching blog post:', error);
      setPost(getDefaultPost(slug));
    } finally {
      setLoading(false);
    }
  };

  const getDefaultPost = (slug) => {
    return {
      title: 'The Future of ERP: AI-Driven Insights for 2025',
      content: `
        <p>Enterprise Resource Planning (ERP) systems have long been the backbone of modern businesses, integrating core processes and providing a unified view of operations. As we approach 2025, artificial intelligence is poised to revolutionize how organizations leverage their ERP investments.</p>
        
        <h2>The AI Revolution in ERP</h2>
        <p>Artificial intelligence is no longer just a buzzword—it's becoming an integral part of enterprise software. Modern ERP systems are incorporating AI capabilities that go far beyond simple automation:</p>
        
        <ul>
          <li><strong>Predictive Analytics:</strong> AI algorithms can analyze historical data to forecast trends, demand patterns, and potential issues before they occur.</li>
          <li><strong>Intelligent Automation:</strong> Routine tasks are automated with greater accuracy, freeing up human resources for strategic work.</li>
          <li><strong>Natural Language Processing:</strong> Users can interact with ERP systems using conversational language, making these powerful tools more accessible.</li>
          <li><strong>Anomaly Detection:</strong> AI can identify unusual patterns in data that might indicate fraud, errors, or opportunities.</li>
        </ul>
        
        <h2>What This Means for Your Business</h2>
        <p>Organizations that embrace AI-enhanced ERP systems will gain significant competitive advantages:</p>
        
        <p><strong>Improved Decision Making:</strong> With AI-powered insights, leaders can make data-driven decisions faster and with greater confidence. Real-time analytics provide a comprehensive view of business performance.</p>
        
        <p><strong>Enhanced Efficiency:</strong> Automated processes reduce manual work and minimize errors. This leads to cost savings and allows teams to focus on value-adding activities.</p>
        
        <p><strong>Better Customer Experience:</strong> AI can help predict customer needs and personalize interactions, leading to higher satisfaction and loyalty.</p>
        
        <h2>Preparing for the Future</h2>
        <p>To take advantage of AI-driven ERP capabilities, organizations should:</p>
        
        <ol>
          <li>Assess current ERP systems for AI readiness</li>
          <li>Develop a data strategy that supports AI initiatives</li>
          <li>Invest in training and change management</li>
          <li>Partner with experienced consultants for smooth implementation</li>
        </ol>
        
        <p>The future of ERP is intelligent, predictive, and proactive. Organizations that start preparing today will be well-positioned to leverage these powerful capabilities tomorrow.</p>
      `,
      category: 'ERP Solutions',
      author: { name: 'TeqDeepSeek Team' },
      publishedAt: new Date().toISOString(),
      featuredImage: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=1200&h=600&fit=crop',
      readTime: '5 min read'
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      {/* Hero Image */}
      <div className="relative h-96">
        <img
          src={post?.featuredImage}
          alt={post?.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-4xl mx-auto">
            <Link to="/blog" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4">
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
            <span className="inline-block px-3 py-1 bg-accent text-white text-sm rounded-full mb-4">
              {post?.category}
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white">
              {post?.title}
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <article className="section bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Meta */}
          <div className="flex items-center gap-6 mb-8 pb-8 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-slate-400" />
              <span className="text-slate-600">{post?.author?.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-slate-400" />
              <span className="text-slate-600">
                {format(new Date(post?.publishedAt), 'MMMM d, yyyy')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-slate-400" />
              <span className="text-slate-600">{post?.readTime}</span>
            </div>
            <div className="ml-auto flex gap-2">
              <button className="p-2 text-slate-400 hover:text-accent transition-colors">
                <Share2 className="w-5 h-5" />
              </button>
              <button className="p-2 text-slate-400 hover:text-accent transition-colors">
                <Bookmark className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Article Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="prose prose-lg max-w-none prose-headings:text-primary prose-a:text-accent"
            dangerouslySetInnerHTML={{ __html: post?.content }}
          />

          {/* Tags */}
          <div className="mt-12 pt-8 border-t border-slate-200">
            <div className="flex flex-wrap gap-2">
              {['ERP', 'AI', 'Digital Transformation', 'Enterprise'].map((tag) => (
                <span key={tag} className="px-3 py-1 bg-slate-100 text-slate-600 text-sm rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="section bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-primary mb-8">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedPosts.map((related) => (
                <Link key={related._id} to={`/blog/${related.slug}`} className="card-hover">
                  <img
                    src={related.featuredImage}
                    alt={related.title}
                    className="w-full h-48 object-cover rounded-t-xl"
                  />
                  <div className="p-6">
                    <span className="text-accent text-sm font-medium">{related.category}</span>
                    <h3 className="text-lg font-bold text-primary mt-2">{related.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPost;
